import React, { useEffect, useMemo, useState } from 'react';
import { Company, Location } from '../../Types';
import useGetData from '../../Hooks/useGetData';
import {
  Form,
  FormSection,
  SectionTitle,
  SectionDescription,
  FormGrid,
  FormTextField,
  FormSelect,
  FormCheckboxGroup,
} from '../../Components/Form';

export interface LocationFormInput {
  name: string;
  CID: string;
  OVCODE: string;
  CustomerName: string;
  CustomerId: string;
  ShipToId: string;
  ContractJobNumber: string;
  SaleLocationId: string;
  longitude: string;
  latitude: string;
  LocationTypeId: string;
  p21_nc_post_order_flag: boolean;
  p21_nc_auto_order_approval_flag: boolean;
  binlocation_flag: boolean;
  p21_nc_post_quote_flag: boolean;
  IsActive: boolean;
}

interface LocationType {
  location_type_id: number;
  name: string;
}

interface Props {
  companies: Company[];
  onSubmit: (formData: LocationFormInput) => void;
  onCancel: () => void;
  defaultCID?: string;
  /** Existing locations used for client-side duplicate checks (name / OVCODE). */
  existingLocations?: Location[];
  /** OVID of the record being edited, so it is excluded from duplicate checks. */
  currentOvid?: number;
  /** Existing location to prefill — switches the form into edit mode. */
  initialData?: Location;
  /** Only Abatix admins may change Active; others don't see the checkbox. */
  canEditActive?: boolean;
}

const emptyForm: LocationFormInput = {
  name: '',
  CID: '',
  OVCODE: '',
  CustomerName: '',
  CustomerId: '',
  ShipToId: '',
  ContractJobNumber: '',
  SaleLocationId: '',
  longitude: '',
  latitude: '',
  LocationTypeId: '',
  p21_nc_post_order_flag: false,
  p21_nc_auto_order_approval_flag: false,
  binlocation_flag: false,
  p21_nc_post_quote_flag: false,
  IsActive: true,
};

const DIGITS = /^\d+$/;

const isOn = (value: unknown) => value === true || Number(value) === 1;
const asText = (value: unknown) => (value === null || value === undefined ? '' : String(value));

const toFormInput = (location: Location): LocationFormInput => ({
  name: asText(location.name),
  CID: asText(location.CID),
  OVCODE: asText(location.OVCODE),
  CustomerName: asText(location.CustomerName),
  CustomerId: asText(location.CustomerId),
  ShipToId: asText(location.ShipToId),
  ContractJobNumber: asText(location.ContractJobNumber),
  SaleLocationId: asText(location.SaleLocationId),
  longitude: asText(location.longitude),
  latitude: asText(location.latitude),
  LocationTypeId: asText(location.LocationTypeId),
  p21_nc_post_order_flag: isOn(location.p21_nc_post_order_flag),
  p21_nc_auto_order_approval_flag: isOn(location.p21_nc_auto_order_approval_flag),
  binlocation_flag: isOn(location.binlocation_flag),
  p21_nc_post_quote_flag: isOn(location.p21_nc_post_quote_flag),
  IsActive: isOn(location.IsActive),
});

const isNonConsignmentType = (typeName?: string): boolean =>
  !!typeName && typeName.toLowerCase().includes('non');

const LocationForm: React.FC<Props> = ({
  companies,
  onSubmit,
  onCancel,
  defaultCID,
  existingLocations,
  currentOvid,
  initialData,
  canEditActive = true,
}) => {
  const isEdit = !!initialData;
  const [formData, setFormData] = useState<LocationFormInput>(
    initialData ? toFormInput(initialData) : { ...emptyForm, CID: defaultCID || '' }
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [locationTypes] = useGetData<LocationType>('/api/location/types');

  useEffect(() => {
    if (defaultCID && !formData.CID) {
      setFormData((prev) => ({ ...prev, CID: defaultCID }));
    }
  }, [defaultCID, formData.CID]);

  // Consignment vs non-consignment is driven by the selected location type name
  // containing "non" — mirrors the old AIMS website behavior.
  const selectedType = useMemo(
    () => (locationTypes || []).find((lt) => String(lt.location_type_id) === formData.LocationTypeId),
    [locationTypes, formData.LocationTypeId]
  );
  const isNonConsignment = isNonConsignmentType(selectedType?.name);

  const clearError = (field: string) =>
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }

    if (name === 'LocationTypeId') {
      const nextType = (locationTypes || []).find((lt) => String(lt.location_type_id) === value);
      const nextIsNonConsignment = isNonConsignmentType(nextType?.name);
      setFormData((prev) => ({
        ...prev,
        LocationTypeId: value,
        // For non-consignment locations the P21 Location ID is derived from the
        // Ship To ID on submit, so clear any manually entered value.
        OVCODE: nextIsNonConsignment ? '' : prev.OVCODE,
      }));
      setErrors((prev) => ({ ...prev, LocationTypeId: '', OVCODE: '' }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    const name = formData.name.trim();
    const shipToId = formData.ShipToId.trim();
    const saleLocationId = formData.SaleLocationId.trim();
    const contractJob = formData.ContractJobNumber.trim();
    const ovcode = formData.OVCODE.trim();

    if (!name) e.name = 'Please enter Location';
    if (!formData.CID) e.CID = 'Please select Company';
    if (!formData.LocationTypeId) e.LocationTypeId = 'Please select Location Type';

    if (!shipToId) e.ShipToId = 'Please enter Ship To ID';
    else if (!DIGITS.test(shipToId)) e.ShipToId = 'Please enter a valid number';

    if (!saleLocationId) e.SaleLocationId = 'Please enter Sale Location ID';
    else if (!DIGITS.test(saleLocationId)) e.SaleLocationId = 'Please enter a valid number';

    if (!contractJob) e.ContractJobNumber = 'Please enter Contract No.';

    // P21 Location ID is only entered for consignment locations.
    if (!isNonConsignment) {
      if (!ovcode) e.OVCODE = 'Please enter P21 Location ID';
      else if (!DIGITS.test(ovcode)) e.OVCODE = 'Please enter a valid number';
    }

    // Duplicate checks against existing locations (excluding the current record).
    const others = (existingLocations || []).filter((l) => l.OVID !== currentOvid);
    if (name && others.some((l) => (l.name || '').trim().toLowerCase() === name.toLowerCase())) {
      e.name = 'Location name already exists';
    }

    const effectiveOvcode = (isNonConsignment ? shipToId : ovcode).toLowerCase();
    if (
      effectiveOvcode &&
      others.some((l) => (l.OVCODE || '').trim().toLowerCase() === effectiveOvcode)
    ) {
      if (isNonConsignment) e.ShipToId = 'Ship To ID already exists for another location';
      else e.OVCODE = 'Location ID already exists for another location';
    }

    return e;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    // Mirror old AIMS: non-consignment locations store the Ship To ID as OVCODE
    // and the P21 non-consignment flags only apply to non-consignment locations.
    onSubmit({
      ...formData,
      OVCODE: (isNonConsignment ? formData.ShipToId : formData.OVCODE).trim(),
      p21_nc_post_order_flag: isNonConsignment ? formData.p21_nc_post_order_flag : false,
      p21_nc_auto_order_approval_flag: isNonConsignment
        ? formData.p21_nc_auto_order_approval_flag
        : false,
      binlocation_flag: isNonConsignment ? formData.binlocation_flag : false,
      p21_nc_post_quote_flag: isNonConsignment ? formData.p21_nc_post_quote_flag : false,
    });
  };

  const flagOptions = [
    ...(canEditActive ? [{ name: 'IsActive', label: 'Active', checked: formData.IsActive }] : []),
    ...(isNonConsignment
      ? [
          {
            name: 'p21_nc_post_order_flag',
            label: 'Post Order',
            checked: formData.p21_nc_post_order_flag,
          },
          {
            name: 'p21_nc_auto_order_approval_flag',
            label: 'Auto Order Approval',
            checked: formData.p21_nc_auto_order_approval_flag,
          },
          { name: 'binlocation_flag', label: 'Bin Location', checked: formData.binlocation_flag },
          {
            name: 'p21_nc_post_quote_flag',
            label: 'Post Quote',
            checked: formData.p21_nc_post_quote_flag,
          },
        ]
      : []),
  ];

  return (
    <Form
      embedded
      hideTitle
      onSubmit={handleSubmit}
      title={isEdit ? 'Edit Location' : 'Add Location'}
      subtitle={isEdit ? 'Update this location\'s details.' : 'Create a new location and assign it to a company.'}
      submitLabel={isEdit ? 'Save Changes' : 'Save Location'}
      onCancel={onCancel}
      disableSubmit={!formData.name || !formData.CID}
    >
      <FormSection>
        <SectionTitle>Company & Identity</SectionTitle>
        <SectionDescription>
          Select the parent company and enter the location name and ID.
        </SectionDescription>
        <FormGrid $columns={2}>
          <FormSelect
            id="company"
            name="CID"
            label="Company"
            value={formData.CID}
            onChange={handleChange as React.ChangeEventHandler<HTMLSelectElement>}
            required
            error={errors.CID}
            placeholder="Select a company"
            options={(companies || []).map((c) => ({ value: String(c.CID), label: c.name }))}
          />
          <FormTextField
            id="locationName"
            name="name"
            label="Location Name"
            value={formData.name}
            onChange={handleChange}
            required
            error={Boolean(errors.name)}
            helperText={errors.name}
            placeholder="Enter location name"
          />
          <FormSelect
            id="locationType"
            name="LocationTypeId"
            label="Location Type"
            value={formData.LocationTypeId}
            onChange={handleChange as React.ChangeEventHandler<HTMLSelectElement>}
            required
            error={errors.LocationTypeId}
            placeholder="Select location type"
            options={(locationTypes || []).map((lt) => ({
              value: String(lt.location_type_id),
              label: `${lt.location_type_id} — ${lt.name}`,
            }))}
          />
          <FormTextField
            id="ovcode"
            name="OVCODE"
            label="P21 Location ID"
            value={formData.OVCODE}
            onChange={handleChange}
            required={!isNonConsignment}
            disabled={isNonConsignment}
            error={Boolean(errors.OVCODE)}
            helperText={
              errors.OVCODE ||
              (isNonConsignment ? 'Derived from Ship To ID for non-consignment locations' : undefined)
            }
            placeholder="e.g. 10038"
          />
        </FormGrid>
      </FormSection>

      <FormSection>
        <SectionTitle>Account Details</SectionTitle>
        <SectionDescription>P21 / account identifiers for this location.</SectionDescription>
        <FormGrid $columns={2}>
          <FormTextField
            name="CustomerName"
            label="Account"
            value={formData.CustomerName}
            onChange={handleChange}
          />
          <FormTextField
            name="CustomerId"
            label="Customer ID"
            value={formData.CustomerId}
            onChange={handleChange}
          />
          <FormTextField
            name="ShipToId"
            label="Ship To ID"
            value={formData.ShipToId}
            onChange={handleChange}
            required
            error={Boolean(errors.ShipToId)}
            helperText={errors.ShipToId}
          />
          <FormTextField
            name="ContractJobNumber"
            label="Contract No."
            value={formData.ContractJobNumber}
            onChange={handleChange}
            required
            error={Boolean(errors.ContractJobNumber)}
            helperText={errors.ContractJobNumber}
          />
          <FormTextField
            name="SaleLocationId"
            label="Sale Location ID"
            value={formData.SaleLocationId}
            onChange={handleChange}
            required
            error={Boolean(errors.SaleLocationId)}
            helperText={errors.SaleLocationId}
          />
        </FormGrid>
      </FormSection>

      <FormSection>
        <SectionTitle>Coordinates</SectionTitle>
        <SectionDescription>Optional latitude and longitude.</SectionDescription>
        <FormGrid $columns={2}>
          <FormTextField
            name="latitude"
            label="Latitude"
            value={formData.latitude}
            onChange={handleChange}
            placeholder="e.g. 32.7767"
          />
          <FormTextField
            name="longitude"
            label="Longitude"
            value={formData.longitude}
            onChange={handleChange}
            placeholder="e.g. -96.7970"
          />
        </FormGrid>
      </FormSection>

      <FormSection>
        <SectionTitle>Flags</SectionTitle>
        <SectionDescription>
          {isNonConsignment
            ? 'Enable the options that apply to this non-consignment location.'
            : canEditActive
              ? 'Consignment locations only require the Active flag.'
              : 'No flags apply to consignment locations.'}
        </SectionDescription>
        <FormCheckboxGroup options={flagOptions} onChange={handleChange} columns={2} />
      </FormSection>
    </Form>
  );
};

export default LocationForm;
